/**
 * Release hero
 *
 * The server renders the launch time in store time (Phoenix, MST all year --
 * Arizona does not observe daylight saving). This adds two things only the
 * browser knows:
 *
 *   1. A countdown, updated each minute ("in 2 days", "in 3 h 12 m").
 *   2. The launch in the visitor's own zone, when it differs from Phoenix:
 *      "5:00 PM MST" reads as "8:00 PM your time" in New York. Intl works
 *      from the real instant, so DST on the visitor's side is handled.
 *
 * Pages are cached, so a visitor can sit on a preview page past launch. At
 * the launch moment the status and action swap to the live versions held in
 * <template>s, rather than leaving a stale "Sunday 5 PM" on screen.
 */
if (!customElements.get('release-hero')) {
  const STORE_ZONE = 'America/Phoenix';

  class ReleaseHero extends HTMLElement {
    connectedCallback() {
      this.launch = Number(this.dataset.launch);
      if (this.dataset.state !== 'preview' || !this.launch) return;

      this.countdown = this.querySelector('[data-release-countdown]');
      this.showLocalTime();
      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const ms = this.launch - Date.now();
      if (ms <= 0) {
        clearInterval(this.timer);
        this.goLive();
        return;
      }
      if (this.countdown) this.countdown.textContent = ReleaseHero.formatRemaining(ms);
    }

    static formatRemaining(ms) {
      const mins = Math.floor(ms / 60000);
      const days = Math.floor(mins / 1440);
      const hours = Math.floor((mins % 1440) / 60);
      const minutes = mins % 60;
      if (days >= 2) return `in ${days} days`;
      if (days === 1) return `in 1 day ${hours} h`;
      if (hours > 0) return `in ${hours} h ${minutes} m`;
      return minutes > 0 ? `in ${minutes} m` : 'in under a minute';
    }

    showLocalTime() {
      const target = this.querySelector('[data-release-local]');
      if (!target || typeof Intl === 'undefined') return;
      const date = new Date(this.launch);
      const opts = { hour: 'numeric', minute: '2-digit' };
      let visitorZone;
      try {
        visitorZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        return;
      }
      const inStore = date.toLocaleTimeString('en-US', { ...opts, timeZone: STORE_ZONE });
      const inVisitor = date.toLocaleTimeString('en-US', { ...opts, timeZone: visitorZone });
      if (inStore === inVisitor) return;

      const dayStore = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: STORE_ZONE });
      const dayVisitor = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: visitorZone });
      target.textContent = dayStore === dayVisitor
        ? `${inVisitor} your time`
        : `${dayVisitor} ${inVisitor} your time`;
    }

    goLive() {
      this.dataset.state = 'live';
      this.classList.remove('release-hero--preview');
      this.classList.add('release-hero--live');

      const status = this.querySelector('[data-release-status]');
      const statusTpl = this.querySelector('[data-release-live-template]');
      if (status && statusTpl) status.replaceChildren(statusTpl.content.cloneNode(true));

      const actions = this.querySelector('[data-release-actions]');
      const actionsTpl = this.querySelector('[data-release-live-actions]');
      if (actions && actionsTpl) actions.replaceChildren(actionsTpl.content.cloneNode(true));
    }
  }

  customElements.define('release-hero', ReleaseHero);
}
